import type { LanguageCode } from '../../../schemas';
import type {
  TranslateBatchInput,
  TranslationProvider,
  TranslationProviderErrorCode,
} from './types';
import { languageCodeValues } from '../../../schemas';
import { TRANSLATION_PROVIDER, TranslationProviderError } from './types';

const ENDPOINT = 'https://translation.googleapis.com/language/translate/v2';
const DETECT_ENDPOINT =
  'https://translation.googleapis.com/language/translate/v2/detect';
const TIMEOUT_MS = 10_000;
const RETRYABLE_HTTP = new Set([429, 500, 502, 503, 504]);

const SUPPORTED_LANGUAGES = new Set<string>(languageCodeValues);

// Our internal code → the code Google's v2 API expects. Google needs a script
// variant for Chinese (`zh` alone is ambiguous / rejected for some scripts);
// legacy V2 mapped it the same way (`translateLanguageMap` in
// travel_planner_back src/common/enum/language-code.ts). Any code without an
// entry is sent through unchanged.
const GOOGLE_TARGET_OVERRIDES: Record<string, string> = {
  zh: 'zh-CN',
};

const toGoogleTarget = (code: string): string =>
  GOOGLE_TARGET_OVERRIDES[code.toLowerCase()] ?? code;

// Google returns BCP-47-ish codes ('en', 'pt', 'zh-CN', 'iw'). Lower-case,
// drop any region suffix ('zh-CN' → 'zh'), and keep it only when it is one of
// our supported `LanguageCode`s — otherwise `null` (treated as "cannot
// detect", never guessed).
const normalizeDetectedLanguage = (raw: unknown): LanguageCode | null => {
  if (typeof raw !== 'string' || raw.trim().length === 0) return null;
  const base = raw.toLowerCase().split('-')[0] ?? '';
  return SUPPORTED_LANGUAGES.has(base) ? (base as LanguageCode) : null;
};

// Mirrors the legacy NestJS impl (travel_planner_back
// src/common/services/translation/core/google-translate-basic-v2.provider.ts)
// but swapped axios → native fetch (Node 20+). Same endpoint, headers,
// timeout, and retryability rules.
export const createGoogleTranslateProvider = (opts: {
  apiKey: string | undefined;
}): TranslationProvider => ({
  async translateBatch({
    texts,
    sourceLanguage,
    targetLanguage,
    format,
  }: TranslateBatchInput): Promise<string[]> {
    // Empty-input fast path: if every entry trims to '', round-trip
    // unchanged without an HTTP call (matches legacy lines 51-57).
    const translatable = texts
      .map((t, i) => ({ i, t }))
      .filter((x) => x.t.trim().length > 0);
    if (translatable.length === 0) {
      return [...texts];
    }

    if (!opts.apiKey) {
      throw new TranslationProviderError(
        'NO_API_KEY',
        'Google Translate API key is not configured',
        { provider: TRANSLATION_PROVIDER.google }
      );
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(ENDPOINT, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'content-type': 'application/json',
          'x-goog-api-key': opts.apiKey,
        },
        body: JSON.stringify({
          q: translatable.map((x) => x.t),
          source: toGoogleTarget(sourceLanguage),
          target: toGoogleTarget(targetLanguage),
          format,
        }),
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new TranslationProviderError(
          'TIMEOUT',
          'Google Translate request timed out',
          { retryable: true, provider: TRANSLATION_PROVIDER.google }
        );
      }
      throw new TranslationProviderError(
        'UNKNOWN',
        'Google Translate request failed',
        { provider: TRANSLATION_PROVIDER.google }
      );
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      const code = mapStatusToCode(response.status);
      throw new TranslationProviderError(code, safeMessage(response.status), {
        statusCode: response.status,
        retryable: RETRYABLE_HTTP.has(response.status),
        provider: TRANSLATION_PROVIDER.google,
      });
    }

    const body = (await response.json()) as {
      data?: { translations?: { translatedText?: string }[] };
    };
    const translations = body?.data?.translations;
    if (!translations || translations.length !== translatable.length) {
      throw new TranslationProviderError(
        'UNKNOWN',
        'Google Translate returned an unexpected response shape',
        { provider: TRANSLATION_PROVIDER.google }
      );
    }

    const out = [...texts];
    translations.forEach((t, idx) => {
      const target = translatable[idx];
      if (target) {
        out[target.i] = t.translatedText ?? '';
      }
    });
    return out;
  },

  async detectLanguage(texts: string[]): Promise<(LanguageCode | null)[]> {
    // Empty-input fast path: entries that trim to '' can't be detected —
    // return null for each without an HTTP call (mirrors translateBatch).
    const detectable = texts
      .map((t, i) => ({ i, t }))
      .filter((x) => x.t.trim().length > 0);
    if (detectable.length === 0) {
      return texts.map(() => null);
    }

    if (!opts.apiKey) {
      throw new TranslationProviderError(
        'NO_API_KEY',
        'Google Translate API key is not configured',
        { provider: TRANSLATION_PROVIDER.google }
      );
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    let response: Response;
    try {
      response = await fetch(DETECT_ENDPOINT, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'content-type': 'application/json',
          'x-goog-api-key': opts.apiKey,
        },
        body: JSON.stringify({ q: detectable.map((x) => x.t) }),
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new TranslationProviderError(
          'TIMEOUT',
          'Google Translate detect request timed out',
          { retryable: true, provider: TRANSLATION_PROVIDER.google }
        );
      }
      throw new TranslationProviderError(
        'UNKNOWN',
        'Google Translate detect request failed',
        { provider: TRANSLATION_PROVIDER.google }
      );
    } finally {
      clearTimeout(timer);
    }

    if (!response.ok) {
      const code = mapStatusToCode(response.status);
      throw new TranslationProviderError(code, safeMessage(response.status), {
        statusCode: response.status,
        retryable: RETRYABLE_HTTP.has(response.status),
        provider: TRANSLATION_PROVIDER.google,
      });
    }

    const body = (await response.json()) as {
      data?: { detections?: { language?: string }[][] };
    };
    const detections = body?.data?.detections;
    if (!detections || detections.length !== detectable.length) {
      throw new TranslationProviderError(
        'UNKNOWN',
        'Google Translate detect returned an unexpected response shape',
        { provider: TRANSLATION_PROVIDER.google }
      );
    }

    // Default every slot to null, then fill the detected (non-empty) inputs
    // back by their original index — Google returns candidates ordered by
    // confidence, so take the first.
    const out: (LanguageCode | null)[] = texts.map(() => null);
    detections.forEach((candidates, idx) => {
      const target = detectable[idx];
      if (!target) return;
      const top = Array.isArray(candidates) ? candidates[0] : undefined;
      out[target.i] = normalizeDetectedLanguage(top?.language);
    });
    return out;
  },
});

const mapStatusToCode = (status: number): TranslationProviderErrorCode => {
  if (status === 403) return 'AUTH_FAILED';
  if (status === 429) return 'RATE_LIMITED';
  if (status === 400) return 'BAD_REQUEST';
  if (RETRYABLE_HTTP.has(status)) return 'UPSTREAM_ERROR';
  return 'UNKNOWN';
};

const safeMessage = (status: number): string => {
  switch (status) {
    case 400:
      return 'Google Translate rejected the request or language code';
    case 403:
      return 'Google Translate API key is invalid, restricted, or the API is disabled';
    case 429:
      return 'Google Translate quota or rate limit exceeded';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'Google Translate provider is temporarily unavailable';
    default:
      return 'Google Translate request failed';
  }
};
